/**
 * Artwork roles: the shape and the sizes every app may ask for. The image pipeline (the media
 * agent now, the editor in the browser later) makes exactly these, once, at upload, so no
 * resizing service is needed and the 5,000-a-month transformation quota is never touched.
 */

export type ImageRole = "poster" | "backdrop" | "thumbnail" | "square" | "logo" | "hero" | "topShelf" | "person";

export interface RoleSpec {
  /** Width ÷ height the image is cropped to; null keeps the source's (logos). */
  aspect: number | null;
  /** Widths made, narrowest first; never wider than the cropped source. */
  widths: number[];
  /** The source must be at least this wide (after cropping) or the upload is refused. */
  minSourceWidth: number;
  /** Keeps transparency (logos); otherwise flattened onto the dominant colour. */
  alpha: boolean;
}

export const ROLES: Record<ImageRole, RoleSpec> = {
  poster: { aspect: 2 / 3, widths: [240, 360, 480, 720, 1080], minSourceWidth: 720, alpha: false },
  backdrop: { aspect: 16 / 9, widths: [640, 960, 1280, 1920, 3840], minSourceWidth: 1920, alpha: false },
  thumbnail: { aspect: 16 / 9, widths: [320, 480, 640, 960, 1280], minSourceWidth: 960, alpha: false },
  square: { aspect: 1, widths: [240, 480, 720, 1080], minSourceWidth: 720, alpha: false },
  logo: { aspect: null, widths: [300, 600, 1200], minSourceWidth: 600, alpha: true },
  hero: { aspect: 12 / 5, widths: [1280, 1920, 2560, 3840], minSourceWidth: 1920, alpha: false },
  /** tvOS Top Shelf (carousel): 2320×720 at 1x, 4640×1440 at 2x. */
  topShelf: { aspect: 2320 / 720, widths: [2320, 4640], minSourceWidth: 2320, alpha: false },
  person: { aspect: 1, widths: [160, 320, 480], minSourceWidth: 320, alpha: false },
};

export interface Crop {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * The largest crop of the role's aspect that fits the source, centred on the focal point
 * (0..1 from the top left) and kept inside the image.
 */
export function cropFor(sourceWidth: number, sourceHeight: number, aspect: number | null, focal = { x: 0.5, y: 0.5 }): Crop {
  if (aspect === null) return { left: 0, top: 0, width: sourceWidth, height: sourceHeight };
  let width = sourceWidth;
  let height = Math.round(width / aspect);
  if (height > sourceHeight) {
    height = sourceHeight;
    width = Math.round(height * aspect);
  }
  const clamp = (v: number, max: number) => Math.min(Math.max(0, v), max);
  const left = clamp(Math.round(focal.x * sourceWidth - width / 2), sourceWidth - width);
  const top = clamp(Math.round(focal.y * sourceHeight - height / 2), sourceHeight - height);
  return { left, top, width, height };
}

/** Widths to make from a crop this wide: the role's, never upscaled; at least one. */
export function widthsFor(role: ImageRole, cropWidth: number): number[] {
  const widths = ROLES[role].widths.filter((w) => w <= cropWidth);
  return widths.length ? widths : [cropWidth];
}

/** Why a source cannot be used for a role, or null. */
export function sourceProblem(role: ImageRole, cropWidth: number): string | null {
  const min = ROLES[role].minSourceWidth;
  return cropWidth < min ? `a ${role} needs a source at least ${min} px wide after cropping (got ${cropWidth} px)` : null;
}
